import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Tool call tracking state interface
export interface ToolCallCard {
  id: string;
  type: 'itinerary' | 'day' | 'stop';
  status: 'loading' | 'complete';
  data: any;
}

// Helper functions for parsing tool call data
const parseToolCallResult = (toolCall: any) => {
  try {
    if (typeof toolCall.result === 'string') {
      return JSON.parse(toolCall.result);
    }
    return toolCall.result;
  } catch {
    return null;
  }
};

const extractToolCallData = (toolCall: any) => {
  const args = typeof toolCall.args?.input === 'string' 
    ? JSON.parse(toolCall.args.input) 
    : toolCall.args?.input || {};
  
  const result = parseToolCallResult(toolCall);
  
  switch (toolCall.toolName) {
    case 'create_itinerary':
      return {
        type: 'itinerary' as const,
        data: {
          title: args.title || result?.title,
          shortDescription: args.shortDescription || result?.shortDescription,
          imageUrl: result?.imageUrl,
        }
      };
    
    case 'add_day':
      return {
        type: 'day' as const,
        data: {
          title: args.title || result?.title,
          description: args.description || result?.description,
          dayNumber: result?.number,
          itineraryImageUrl: result?.itinerary?.imageUrl,
        }
      };
    
    case 'add_stop':
      return {
        type: 'stop' as const,
        data: {
          title: args.name || result?.title,
          highlight: args.highlights || result?.highlight,
          description: args.description || result?.description,
          imageUrl: result?.imageUrl,
          index: result?.index,
        }
      };
    
    default:
      return null;
  }
};

export function useToolCallHandling() {
  const { t } = useTranslation();
  const [allToolCalls, setAllToolCalls] = useState<ToolCallCard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get the currently visible tool call
  const currentToolCall = allToolCalls.length > 0 && currentIndex < allToolCalls.length 
    ? allToolCalls[currentIndex] 
    : null;

  // Auto-advance to next tool call when current one completes
  useEffect(() => {
    if (currentToolCall?.status === 'complete') {
      const timer = setTimeout(() => {
        setCurrentIndex(prev => {
          const nextIndex = prev + 1;
          // If we've shown all cards, reset
          if (nextIndex >= allToolCalls.length) {
            setAllToolCalls([]);
            return 0;
          }
          return nextIndex;
        });
      }, 2000); // Show completed card for 2 seconds

      return () => clearTimeout(timer);
    }
  }, [currentToolCall?.status, currentToolCall?.id, allToolCalls.length]);

  const addOrUpdateToolCall = useCallback((toolCall: any) => {
    const extractedData = extractToolCallData(toolCall);
    if (!extractedData) return;

    const newCard: ToolCallCard = {
      id: toolCall.toolCallId,
      type: extractedData.type,
      status: toolCall.status === 'complete' ? 'complete' : 'loading',
      data: extractedData.data,
    };

    setAllToolCalls(prev => {
      const existingIndex = prev.findIndex(tc => tc.id === toolCall.toolCallId);
      if (existingIndex >= 0) {
        // Update existing card
        const updated = [...prev];
        updated[existingIndex] = newCard;
        return updated;
      } else {
        // Add new card
        return [...prev, newCard];
      }
    });
  }, []);

  const clearToolCalls = useCallback(() => {
    setAllToolCalls([]);
    setCurrentIndex(0);
  }, []);

  const handleToolCall = useCallback(async ({ toolCall }: { toolCall: any }) => {
    // Add or update tool call card
    addOrUpdateToolCall(toolCall);
    
    // Handle itinerary creation tool calls when result is available  
    if (toolCall.toolName === 'create_itinerary' && 
        (toolCall as any).status === 'complete' && 
        (toolCall as any).result) {
      
      // Parse the result to extract the itinerary ID
      const result = parseToolCallResult(toolCall);
      const itineraryId = result?.id;
      const uuidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
      
      // Check if we're already on this itinerary's page
      const currentPath = window.location.pathname;
      const isAlreadyOnItinerary = currentPath.includes(`/itineraries/${itineraryId}`);
      
      if (itineraryId && uuidRegex.test(itineraryId) && !isAlreadyOnItinerary) {
        // Store the navigation details to execute later, preserving threadId
        const searchParams = new URLSearchParams();
        const currentThreadId = new URLSearchParams(window.location.search).get('chatThreadId');
        if (currentThreadId && currentThreadId !== 'new') {
          searchParams.set('chatThreadId', currentThreadId);
        }
        
        (window as any).pendingItineraryNavigation = {
          itineraryId,
          path: `/itineraries/${itineraryId}/edit`,
          search: Object.fromEntries(searchParams.entries())
        };

        toast.success(t('common.chat.itineraryCreated'));
      }
    }
  }, [addOrUpdateToolCall, t]);

  return {
    activeToolCalls: currentToolCall ? [currentToolCall] : [],
    currentToolCall,
    handleToolCall,
    clearToolCalls,
  };
} 